import React, { useCallback, useMemo, useState } from "react";

import ArrowRightWhite from "@vahak/core/dist/icons/arrow-right-white.svg";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";

import { useRemoteConfig } from "../../../../context/RemoteConfigContext";
import { IEventTrackerProp } from "../../../../types/events";
import Styles from "./QuoteFormCard.module.scss";
import useHomepageV2Translation from "../../hooks/useHomepageV2Translation";
import { LoadQuoteValues } from "../../load-quote";
import LoadQuoteFormFields from "./LoadQuoteFormFields";
import { QUOTE_FORM_LCV_LORRY_TYPE } from "./loadQuoteForm.constants";
import { useLoadQuoteLocationForm } from "./useLoadQuoteLocationForm";

type QuoteFormCardProps = IEventTrackerProp & {
    onOpenCompactPosting: (values: LoadQuoteValues) => void;
    isCompactPostingOpen: boolean;
    isCompactPostingOpening?: boolean;
};

const QuoteFormCard = ({
    trackEvent,
    eventData,
    onOpenCompactPosting,
    isCompactPostingOpen,
    isCompactPostingOpening = false
}: QuoteFormCardProps) => {
    const { t } = useHomepageV2Translation();
    const { isLoaded: isRemoteConfigLoaded } = useRemoteConfig();
    const [values, setValues] = useState<LoadQuoteValues>({
        source: undefined,
        destination: undefined,
        lorryType: QUOTE_FORM_LCV_LORRY_TYPE,
        tonnes: ""
    });

    const handleChange = useCallback((patch: Partial<LoadQuoteValues>) => {
        setValues((current) => ({ ...current, ...patch }));
    }, []);

    const {
        handleChange: trackAndChange,
        handleLocationInput,
        locationMessages,
        isFormValid,
        focusFirstInvalidField
    } = useLoadQuoteLocationForm({
        values,
        onChange: handleChange,
        sourceScreen: WEB_V2_SOURCE_SCREENS.HOME_PAGE,
        isRemoteConfigLoaded,
        t,
        trackEvent,
        eventData,
        prefillFromContext: true
    });

    const homeEventData = useMemo(
        () => ({ ...eventData, source_screen: WEB_V2_SOURCE_SCREENS.HOME_PAGE }),
        [eventData]
    );

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isFormValid) {
            focusFirstInvalidField();
            return;
        }

        trackEvent?.(GA4EventNames.WEB_V2.check_prices_clicked, homeEventData);
        onOpenCompactPosting({ ...values });
    };

    return (
        <div className={Styles.card}>
            <form className={Styles.fields} onSubmit={handleSubmit} noValidate>
                <LoadQuoteFormFields
                    variant="hero"
                    values={values}
                    labels={{
                        pickupLabel: t("homepageV2:hero.quoteForm.pickupLabel"),
                        dropLabel: t("homepageV2:hero.quoteForm.dropLabel"),
                        pickupPlaceholder: t("homepageV2:hero.quoteForm.pickupCity"),
                        dropPlaceholder: t("homepageV2:hero.quoteForm.dropCity")
                    }}
                    locationMessages={locationMessages}
                    onChange={trackAndChange}
                    onPickupInputChange={(value) => handleLocationInput("pickup", value)}
                    onDropInputChange={(value) => handleLocationInput("drop", value)}
                />

                <button type="submit" className={Styles.cta} disabled={isCompactPostingOpen || isCompactPostingOpening}>
                    <span>{t("homepageV2:hero.quoteForm.cta")}</span>
                    <ArrowRightWhite className={Styles.ctaIcon} aria-hidden />
                </button>
            </form>

            <p className={Styles.disclaimer}>{t("homepageV2:hero.quoteForm.disclaimer")}</p>
        </div>
    );
};

export default QuoteFormCard;
